import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  EditorState,
  getRawObject,
  MatchObject,
  TooltipState,
} from "@/components/pages/tool-components/shared/editor/helpers";

export const useToolEditor = () => {
  const [editor, setEditor] = useState<EditorState>({ text: "", html: null, isChecked: false });
  const [tooltip, setTooltip] = useState<TooltipState>({ x: null, y: null, isShown: false });
  const [matches, setMatches] = useState<MatchObject[]>([]);
  const editorRef = useRef<HTMLDivElement>(null);

  const handleEditorClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLDivElement;

      if (!editorRef.current) return;
      if (target.classList.contains("editor-highlight")) {
        const id = +target.id;
        const match = matches.find((item) => item.id == id);

        target.classList.add("highlight-focused");

        setTooltip({
          ...tooltip,
          data: match,
          x: target.offsetLeft,
          y: target.offsetTop - editorRef.current.scrollTop + target.getBoundingClientRect().height + 4,
          isShown: true,
        });
      }
    },
    [matches, tooltip]
  );

  const handleEditorPaste = useCallback((e: ClipboardEvent) => {
    let paste = (e.clipboardData || window.clipboardData).getData("text");
    const selection = window.getSelection();

    if (!selection?.rangeCount) return false;

    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(paste));
    selection.collapseToEnd();

    e.preventDefault();
    setEditor((prev) => ({
      ...prev,
      text: editorRef.current?.innerText || "",
    }));
  }, []);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // TODO: handle double highlight click
      const closest = target.closest("#tooltip");

      if (!closest && tooltip.isShown) {
        Array.from(document.querySelectorAll(".editor-highlight")).map((el) =>
          el.classList.remove("highlight-focused")
        );
        setTooltip((t) => ({ ...t, isShown: false }));
      }
    },
    [tooltip.isShown]
  );
  useEffect(() => {
    const editorElement = editorRef.current;

    if (!editorElement) return;

    editorElement.addEventListener("click", handleEditorClick);
    editorElement.addEventListener("paste", handleEditorPaste);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      editorElement.removeEventListener("click", handleEditorClick);
      editorElement.removeEventListener("paste", handleEditorPaste);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleEditorClick, handleClickOutside, handleEditorPaste]);

  const handleIgnore = (matchId: string) => {
    setMatches(matches.map((object) => (object.id == matchId ? { ...object, isIgnored: true } : object)));
    document.getElementById(matchId)?.classList.remove("editor-highlight", "highlight-focused");
    setTooltip({ ...tooltip, isShown: false });
  };

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = editor.html || "";
  }, [editor.html]);

  const handleReplace = (matchId: string, replacement: string) => {
    const replacementObject = { id: matchId, value: replacement, isCorrect: true };
    const replacementNode = document.getElementById(matchId);

    setMatches(matches.map((object) => (object.id == matchId ? replacementObject : object)));
    replacementNode?.insertAdjacentHTML("beforebegin", getRawObject(replacementObject));
    replacementNode?.remove();
    setTooltip({ ...tooltip, isShown: false });

    setEditor((prev) => ({
      ...prev,
      text: editorRef.current?.innerText?.trim() || "",
    }));
  };

  const handleEditorInput = (
    e: KeyboardEvent<HTMLDivElement>,
    validateInput: () => void,
    beforeCallback?: Function
  ) => {
    beforeCallback?.();
    setEditor((prev) => ({
      ...prev,
      text: (e.target as HTMLElement).innerText.trim(),
    }));
    setTooltip({ ...tooltip, isShown: false });
    setTimeout(() => {
      validateInput();
    }, 0);

    const selection = window.getSelection();
    const selectionNode = selection?.getRangeAt(0).startContainer;
    const selectionParent = selectionNode?.parentElement;

    if (selectionParent?.classList.contains("editor-fragment")) {
      // delete highlight
      selectionParent.classList.remove("editor-highlight");
    }
  };

  return {
    editorRef,
    editor,
    setEditor,
    matches,
    setMatches,
    tooltip,
    setTooltip,
    handleIgnore,
    handleReplace,
    handleEditorInput,
  };
};
